import { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router-dom';
import { classNameTriskeleParser } from '../../utilities/customStyles';
import CalendarDialog from '../../screens/Dashboard/components/CalendarDialog';
import 'react-loading-skeleton/dist/skeleton.css';
import { decrementSevenDays, incrementSevenDays, setSelectedDate } from '../../redux/actions/selectedDateActions';
import { setSelectedSevenDays } from '../../redux/actions/selectedSevenDaysActions';
import globalContext from '../../context/GlobalContext';
import { compareDates, formatDate, formatToApiDateUTC, formatTodaydDate } from '../../utilities/dates';
import {
  Body3400C,
  CALENDAR_ICON, CALENDAR_TODAY_ICON, MONTH_SELECTOR_FRONT_ICON, MONTH_SELECTOR_ICON
} from '../../utilities/generatedClasses';
import GeneralAccess from '../../dataAccess/GeneralAccess';
import { overlayTopSide, unOverlayTopSide } from '../../utilities/utils';

const WeekHeader = () => {
  const dispatch = useDispatch();

  const [days, setDays] = useState(null);
  const [open, setOpen] = useState(false);
  const [weekAt, setWeekAt] = useState(0);
  const [todayDate, setTodayDate] = useState(null);
  const [isViewHidden, setViewHidden] = useState(false);

  const topFilterData = useSelector(state => state.widgetFiltes);
  const context = useContext(globalContext);

  const location = useLocation();

  useEffect(
    () => {
      if (location.pathname.includes('analytic') || location.pathname.includes('recommendations/add') || location.pathname.includes('home/editprofile') || location.pathname.includes('home/paymentsubmission')) {
        setViewHidden(true);
      } else {
        setViewHidden(false);
      }
    },
    [location]
  );

  useEffect(() => {
    callTriskeleApi();
  }, [weekAt, topFilterData.selectedDate]);

  useEffect(() => {
    if (location.pathname.includes('home/dashboard') || location.pathname === '/home/recommendations') {
      let d = new Date();

// UTC parts
let month = d.getMonth() + 1;  // months are zero-based
let day = d.getDate();
let year = d.getFullYear();

// Format like "8/27/2025" (same as en-US without padding)
let formatted = `${month}/${day}/${year}`;
      dispatch(setSelectedDate(formatted));
    }
  }, [location]);

  const getYear = (date) => {
    const splitDate = date.split('/');
    if (splitDate && splitDate.length === 3) return splitDate[2];
    return 'date';
  };

  const callTriskeleApi = () => {
    if (topFilterData.selectedDate) {
      GeneralAccess.getWeeklyView(topFilterData.selectedDate, weekAt)
        .then((response) => {
          if (response.data && response.data.length > 0) {
            if (todayDate == null) {
              let _todayDate = formatTodaydDate(topFilterData.selectedDate, 0)
              setTodayDate(formatToApiDateUTC(_todayDate));
            }
            setWeekDaysFromResponse(response, false);
          }
        })
        .catch((error) => {
          console.error(error);

          context.navigation.push('/maintenance');
        });
    }
  };

  const setWeekDaysFromResponse = (response, isResetingDate) => {
    const loadedDays = response.data.map((el) => {
      const weekClass = classNameTriskeleParser.bind(this, el.VisualNameOne, el.VisualTypeOne);
      el.triskeleClass = {
        selected: weekClass('Rectangle', 'Selected'),
        unselected: weekClass('Rectangle', 'Unselected'),
        text1: weekClass('TextLine1'),
        text2: weekClass('TextLine2'),
        text3: weekClass('TextLine3'),
        line: weekClass('Line')
      };
      el.Day = parseInt(el.Day, 0);
      return el;
    });
    setDays(loadedDays);
    dispatch(setSelectedSevenDays(loadedDays));
    if (isResetingDate) {
      setDateSelected(3, loadedDays);
    }
  };

  const setDateSelected = (index, loadedDays = null) => {
    let day = days[index];
    if (loadedDays) {
      day = loadedDays[index];
    }
    // day.Date
    const currentSetDate = formatDate(day.Date);
    dispatch(setSelectedSevenDays(days));
    dispatch(setSelectedDate(currentSetDate));
    setWeekAt(0);
  };

  const showCalendar = () => {
    overlayTopSide();
    setOpen(true);
  };

  const handleClose = (value) => {
    unOverlayTopSide();
    setOpen(false);
  };

  const incrementWeek = () => {
    setWeekAt(weekAt + 1);
    dispatch(incrementSevenDays());
  };

  const decrementWeek = () => {
    setWeekAt(weekAt - 1);
    dispatch(decrementSevenDays());
  };

  const resetCalendar = () => {
    dispatch(setSelectedDate(todayDate));
  };

  return (
    !isViewHidden && (
    <div className="week-header">
      <div className="week-header-main">
        <span onClick={() => decrementWeek()} className={`week-header-main-row-left-arrow ${MONTH_SELECTOR_ICON}`} aria-hidden />
        {!days && /* skeleton */ <Skeleton inline borderRadius="1rem" className="week-header-skeleton-row-button" count={7} />}
        {days// regular
          && days.map((day, index) => (
            <div
              key={index}
              onClick={() => setDateSelected(index)}
              aria-hidden
              className={`week-header-main-row-button ${!compareDates(topFilterData.selectedDate, days[index].Date) ? day.triskeleClass.unselected : day.triskeleClass.selected}`}
            >
              <div className={day.triskeleClass.text1}>{day.DayOfWeek}</div>
              <div className={day.triskeleClass.text2}>{day.Day}</div>
              <div
                className={`${day.triskeleClass.text3} ${compareDates(todayDate, days[index].Date)
                  ? day.triskeleClass.line
                  : ''}`}
              >
                {day.Month}
              </div>
            </div>
          ))}
        <span
          onClick={() => incrementWeek()}
          className={`week-header-main-row-right-arrow ${MONTH_SELECTOR_FRONT_ICON}`}
          aria-hidden />
        <div style={{ padding: '15px 0' }} className='week-header-main-picker-year-container'>
          {!days && <Skeleton borderRadius="1rem" className="week-header-skeleton-picker-year" />}
          {days
            && (
            <div className="week-header-main-picker-year custom-space-between" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="week-header-main-picker-year" onClick={() => resetCalendar()} aria-hidden>
                <div
                  className={`week-header-main-picker-year-calendar ${CALENDAR_TODAY_ICON}`}
                   />
              </div>
              <div className="year-date">
                <span className={`week-header-main-picker-year-text ${Body3400C}`}>{getYear(topFilterData.selectedDate)}</span>
              </div>
              <div className="week-header-main-picker-year" onClick={() => showCalendar()} aria-hidden>
                <div id="calendarIconButton" className={`week-header-main-picker-year-calendar ${CALENDAR_ICON}`} />
              </div>
            </div>
            )}
        </div>
      </div>
      <div className="week-header-main-picker" />


      <div />
      <CalendarDialog open={open} onClose={handleClose} />
    </div>
    )
  );
};
export default WeekHeader;
